import type { FeedbackMetadata } from "@/lib/api-extra";
import { toJpeg } from "html-to-image";

const MAX_SCREENSHOT_DATA_URL_LENGTH = 850_000;
const PORTAL_SELECTORS = ["[data-radix-portal]", "[data-vaul-drawer-wrapper]", "[data-vaul-drawer]"];

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message.slice(0, 500);
  }
  return "No se pudo renderizar la captura";
}

function shouldIgnoreElement(element: Element): boolean {
  return element instanceof HTMLElement && !!element.closest('[data-feedback-ignore="true"]');
}

function getPortalNodes(): HTMLElement[] {
  const nodes = PORTAL_SELECTORS.flatMap((selector) => Array.from(document.querySelectorAll<HTMLElement>(selector)));

  return nodes.filter((node, index) => {
    if (shouldIgnoreElement(node)) {
      return false;
    }

    if (node.childElementCount === 0) {
      return false;
    }

    return !nodes.some((otherNode, otherIndex) => otherIndex !== index && otherNode.contains(node));
  });
}

function createCaptureSurface(): {
  target: HTMLElement;
  captureTarget: "capture-root" | "body";
  cleanup: () => void;
} {
  const root = document.getElementById("root");
  if (!root) {
    return {
      target: document.body,
      captureTarget: "body",
      cleanup: () => {},
    };
  }

  const portalNodes = getPortalNodes();
  const captureSurface = document.createElement("div");
  captureSurface.setAttribute("data-feedback-capture-surface", "true");
  Object.assign(captureSurface.style, {
    position: "fixed",
    inset: "0",
    width: `${window.innerWidth}px`,
    height: `${window.innerHeight}px`,
    overflow: "hidden",
    pointerEvents: "none",
    isolation: "isolate",
    zIndex: "-2147483647",
    background: getComputedStyle(document.body).backgroundColor || "#ffffff",
  });

  const rootClone = root.cloneNode(true) as HTMLElement;
  rootClone.removeAttribute("id");
  Object.assign(rootClone.style, {
    width: "100%",
    minHeight: "100%",
  });
  captureSurface.appendChild(rootClone);

  for (const portalNode of portalNodes) {
    captureSurface.appendChild(portalNode.cloneNode(true));
  }

  document.body.appendChild(captureSurface);

  return {
    target: captureSurface,
    captureTarget: portalNodes.length > 0 ? "body" : "capture-root",
    cleanup: () => {
      captureSurface.remove();
    },
  };
}

async function renderScreenshot(target: HTMLElement): Promise<string | null> {
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 1);
  const qualities = [0.82, 0.7, 0.55, 0.4];

  for (const quality of qualities) {
    const dataUrl = await toJpeg(target, {
      quality,
      pixelRatio,
      cacheBust: true,
      skipAutoScale: true,
      skipFonts: true,
      width: window.innerWidth,
      height: window.innerHeight,
      filter: (node) => {
        if (shouldIgnoreElement(node)) return false;
        // Skip the <style> Radix injects to set pointer-events:none on body
        // so it doesn't interfere with computed styles in the clone.
        if (node instanceof HTMLElement && node.tagName === "STYLE" && node.textContent?.includes("pointer-events")) return false;
        return true;
      },
    });
    if (dataUrl && dataUrl.length <= MAX_SCREENSHOT_DATA_URL_LENGTH) {
      return dataUrl;
    }
  }

  return null;
}

export async function captureFeedbackMetadata(): Promise<FeedbackMetadata> {
  const metadata: FeedbackMetadata = {
    app: "web",
    pageTitle: document.title,
    capturedAt: new Date().toISOString(),
    currentUrl: window.location.href,
    userAgent: navigator.userAgent,
    language: navigator.language,
    devicePixelRatio: window.devicePixelRatio,
    captureStatus: "failed",
    captureTarget: "none",
    captureError: null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
  };

  const surface = createCaptureSurface();

  try {
    const dataUrl = await renderScreenshot(surface.target);

    if (dataUrl) {
      metadata.screenshotDataUrl = dataUrl;
      metadata.captureStatus = "success";
      metadata.captureTarget = surface.captureTarget;
    } else {
      metadata.captureStatus = "fallback";
      metadata.captureTarget = surface.captureTarget;
      metadata.captureError = "La captura superó el tamaño máximo permitido";
    }
  } catch (error) {
    metadata.screenshotDataUrl = null;
    metadata.captureStatus = "fallback";
    metadata.captureTarget = surface.captureTarget;
    metadata.captureError = getErrorMessage(error);
  } finally {
    surface.cleanup();
  }

  return metadata;
}
